import React from 'react';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import { Theme } from '@mui/material';
import { Tone } from '../../../theme';

const useStyles = createUseStyles((theme: Theme) => {
    const { color, radius } = theme.custom;
    const tones = Object.fromEntries((Object.keys(color.tone) as Tone[]).map((tone) => [
        `tone-${tone}`,
        {
            backgroundColor: color.tone[tone].soft,
            color: color.tone[tone].onSoft,
            '& $dot': { backgroundColor: color.tone[tone].main },
        },
    ])) as Record<`tone-${Tone}`, { backgroundColor: string; color: string; '& $dot': { backgroundColor: string } }>;
    return {
        root: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            height: 26,
            padding: '0 10px',
            borderRadius: radius.sm,
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1,
            letterSpacing: '0.01em',
            whiteSpace: 'nowrap',
            verticalAlign: 'middle',
            maxWidth: '100%',
        },
        small: { height: 22, padding: '0 8px', fontSize: 11 },
        dot: { width: 6, height: 6, borderRadius: '50%', flexShrink: 0 },
        label: { overflow: 'hidden', textOverflow: 'ellipsis' },
        ...tones,
    };
});

export interface StatusChipProps {
    tone?: Tone;
    size?: 'sm' | 'md';
    /** Leading colored dot (use when several chips of the same tone sit together). */
    dot?: boolean;
    className?: string;
    title?: string;
    children: React.ReactNode;
}

/** Status pill: translucent background + readable text, the same in both themes. */
const StatusChip = ({ tone = 'neutral', size = 'md', dot = false, className, title, children }: StatusChipProps) => {
    const classes = useStyles();
    return (
        <span className={classNames(classes.root, classes[`tone-${tone}`], { [classes.small]: size === 'sm' }, className)} title={title}>
            {dot && <span className={classes.dot} aria-hidden="true" />}
            <span className={classes.label}>{children}</span>
        </span>
    );
};

export default StatusChip;
